import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, AlertTriangle, ClipboardCheck } from "lucide-react";
import { getReconciliacaoKitsAdmissao } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { DEPARTAMENTOS_GESTAO_ORCAMENTARIA } from "../../constants/departamentosOrcamento";
import { MESES_ABREV } from "../../utils/orcamento";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none",
  };
}

// Auditoria de Kits de Admissão: cruza "novas contratações" (Mão de Obra, vigencia_inicio do
// colaborador) com "Kits Admissão lançados" (Despesas/Custos Gerais, ver kit_admissao_svc.py),
// por departamento e mês — sinaliza divergência em qualquer direção (mais kit que contratação, ou
// o contrário). 1 fetch traz os 13 departamentos de uma vez (GET /orcamento/kits-admissao/
// reconciliacao); o seletor de departamento aqui é só um filtro client-side sobre esse resultado.
export default function AuditoriaKitsAdmissaoView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  // Mesmo padrão de ano-padrão de Despesas/Custos Gerais e Mão de Obra (ANO_ATUAL+1, o ano de
  // planejamento) — Kit Admissão é lançado dentro dessas telas, então o padrão aqui precisa
  // bater com o delas, senão a auditoria mostra 0 kits só por estar olhando o ano errado.
  const [ano, setAno] = useState(ANO_ATUAL + 1);
  const [departamento, setDepartamento] = useState(DEPARTAMENTOS_GESTAO_ORCAMENTARIA[0]);
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    setCarregando(true);
    setErro("");
    getReconciliacaoKitsAdmissao(ano)
      .then(setLinhas)
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [ano]);

  const linhaDepartamento = useMemo(
    () => linhas.find((l) => l.departamento === departamento),
    [linhas, departamento]
  );
  const meses = linhaDepartamento?.meses || [];
  const totalContratacoes = meses.reduce((acc, m) => acc + m.contratacoes, 0);
  const totalKits = meses.reduce((acc, m) => acc + m.kits_lancados, 0);
  const totalDiferenca = totalContratacoes - totalKits;

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Auditoria de Kits de Admissão</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Auditoria de Kits de Admissão</h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Novas contratações (Mão de Obra) x Kits Admissão lançados (Despesas/Custos Gerais), por departamento e mês — divergência em qualquer direção fica destacada.
            </p>
          </div>

          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <div style={{ maxWidth: 360 }}>
            <select value={departamento} onChange={(e) => setDepartamento(e.target.value)} style={{ ...inputStyle(C), width: "100%" }}>
              {DEPARTAMENTOS_GESTAO_ORCAMENTARIA.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <ClipboardCheck size={15} color={C.brand} />
              <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>{departamento} · {ano}</h2>
              {totalDiferenca !== 0 && (
                <span style={{
                  marginLeft: "auto", fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999,
                  background: `${C.warning}22`, color: C.warning,
                }}>
                  Diferença no ano: {totalDiferenca > 0 ? "+" : ""}{totalDiferenca}
                </span>
              )}
            </div>

            {carregando ? (
              <p style={{ fontSize: 12, color: C.muted }}>Carregando…</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "8px 10px", color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>Mês</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>Contratações</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>Kits Lançados</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: C.muted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>Diferença</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meses.map((m) => (
                      <tr key={m.mes} style={{ background: m.diferenca !== 0 ? `${C.warning}14` : "transparent" }}>
                        <td style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}` }}>{MESES_ABREV[m.mes - 1]}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: `1px solid ${C.border}` }}>{m.contratacoes}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: `1px solid ${C.border}` }}>{m.kits_lancados}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700, color: m.diferenca === 0 ? C.success : C.warning }}>
                          {m.diferenca > 0 ? "+" : ""}{m.diferenca}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 800 }}>
                      <td style={{ padding: "8px 10px" }}>Total {ano}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right" }}>{totalContratacoes}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right" }}>{totalKits}</td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: totalDiferenca === 0 ? C.success : C.warning }}>
                        {totalDiferenca > 0 ? "+" : ""}{totalDiferenca}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
